import { AddOutlined, ArrowUpwardOutlined, MicOutlined } from '@mui/icons-material';
import { Button, ButtonProps, Stack, SxProps, Tooltip } from '@mui/material';
import { PropsWithChildren, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { Metadata } from '../Chat/chat.types';
import { MessageVariant } from '../Chat/ChatUserMessage/ChatUserMessage.types';
import { ProgressIndicator } from '../ProgressIndicator/ProgressIndicator';
import { I18nNamespace } from '~/common/types';

import { AttachmentList } from './components/AttachmentList/AttachmentList';
import { MessageInput } from './components/MessageInput/MessageInput';
import { ConversationInputRoot } from './ConversationInput.styled';
import { useConversationInputAttachments } from './hooks/useConversationInputAttachments';
import { useConversationInputFocus } from './hooks/useConversationInputFocus';
import { useChatInputMessage } from './hooks/useConversationInputMessage';

interface ConversationInputProps {
  onSubmit(args: { text: string }, metadata?: Metadata): Promise<void> | void;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  actions?: ReactNode;
  sx?: SxProps;
  enableAttachments?: boolean;
  forceAutofocus?: boolean;
  slotProps?: Union.Nullable<{
    submit?: Omit<ButtonProps, 'disabled' | 'loading'>;
  }>;
}

/**
 * The base ConversationInput component for agent chat interfaces.
 * This component consists of a text area and a send button.
 * Additional features are file-attachments and audio-recording/transcription
 */
export const ConversationInput = ({
  placeholder,
  isLoading,
  disabled,
  actions,
  enableAttachments = false,
  forceAutofocus = false,
  onSubmit,
  sx,
  slotProps,
}: PropsWithChildren<ConversationInputProps>): ReactNode => {
  const { t } = useTranslation(I18nNamespace.conversation);

  const fullyDisabled = isLoading || disabled;
  const handleSubmit = () => {
    if (isUploading) return;
    onSubmit(
      { text: message },
      hasAttachments ? { type: MessageVariant.FILE_ATTACHMENT, files: attachments } : undefined,
    );
    // clear message & attachments after submit
    clearMessage();
    clearAttachments();
  };

  const {
    isUploading,
    hasAttachments,
    attachments,
    clearAttachments,
    onDelete,
    buttonProps,
    inputProps,
    dropZoneProps,
  } = useConversationInputAttachments({ enableAttachments });
  const { hasMessage, message, clearMessage, ...messageHandleInputProps } = useChatInputMessage({ handleSubmit });

  const isSubmitDisabled = useMemo(() => {
    return isUploading || fullyDisabled || (!hasMessage && !hasAttachments);
  }, [isUploading, fullyDisabled, hasMessage, hasAttachments]);

  // ref to maintain focus on the input (points to MUI Input wrapper div)
  const inputRef = useConversationInputFocus({ forceAutofocus, disabled });

  const messageInputProps = {
    placeholder,
    inputRef,
    disabled: fullyDisabled, // only disable user text input when entire component is disabled
    ...messageHandleInputProps,
  };

  return (
    <ConversationInputRoot elevation={1} sx={sx} {...dropZoneProps}>
      {enableAttachments ? (
        <>
          <AttachmentList
            attachments={attachments}
            onDelete={onDelete}
            // Display indicator when uploading as last element of the attachments
            // Agreed with Design on a fixed width of 100px plus additional text
            uploadIndicator={
              isUploading ? <ProgressIndicator message={t('AttachmentList.uploading')} progressBarWidth={100} /> : null
            }
          />
          {/* non-visible input for file upload handling */}
          <input {...inputProps} />
        </>
      ) : null}

      {/* actual multiline text input for the user message */}
      <MessageInput {...messageInputProps}></MessageInput>

      {/* layouting buttons for user interactions */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <>
            {enableAttachments && (
              <Tooltip title={t('ConversationInput.action.addAttachment')} placement="top">
                <Button variant="text" size="medium" sx={{ p: 2 }} disabled={disabled} {...buttonProps}>
                  <AddOutlined />
                </Button>
              </Tooltip>
            )}
            {actions ? actions : null}
          </>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button disabled variant="text" size="medium" sx={{ p: 2 }}>
            <MicOutlined />
          </Button>
          <Tooltip title={t('ConversationInput.action.sendMessage')} placement="top">
            <Button
              color="primary"
              variant="contained"
              size="medium"
              sx={{ p: 2 }}
              onClick={handleSubmit}
              {...slotProps?.submit}
              // loading and disabled states are controlled within the component
              // and should not be overridden by the parent in any way
              loading={isLoading}
              disabled={fullyDisabled || isSubmitDisabled}
            >
              <ArrowUpwardOutlined />
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
    </ConversationInputRoot>
  );
};
